import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { Autocomplete, AvatarGroup } from "@material-ui/lab";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import ShareOutlinedIcon from "@material-ui/icons/ShareOutlined";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import MailOutlineIcon from "@material-ui/icons/MailOutline";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import { useDispatch, useSelector } from "react-redux";
import { fetchAllApps } from "../features/allApps/allAppsSlice";
import { selectAllTriggers, selectAllActions } from "../features/allApps/allAppsSlice";
import {
  cloneAgentToTenants,
  createAgent,
  deleteAgent,
  getAgentUsage,
  fetchAiUsage,
  listAccessibleTenants,
  listAgents,
  shareAgentByEmail,
} from "../features/agents/agentApi";
import AgentsAppBar, { AGENTS_APP_BAR_OFFSET_PX } from "../components/Agents/AgentsAppBar";
import AgentUsageMeter, { AgentUsageMeterSkeleton } from "../components/Agents/AgentUsageMeter";
import AgentAiCreditMeter, { AgentAiCreditMeterSkeleton } from "../components/Agents/AgentAiCreditMeter";

const WORKLOAD_LOGO = "https://cdn.workload.co/wp-content/uploads/new-logo-only-512-512.png";

const useDialogStyles = makeStyles((theme) => ({
  paper: {
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    overflow: "hidden",
    backgroundColor: theme.palette.background.paper,
  },
  accentBar: {
    height: 4,
    backgroundColor: theme.palette.primary.main,
  },
  headerRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1.5),
  },
  logo: {
    width: 36,
    height: 36,
    flexShrink: 0,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
  },
  title: {
    fontWeight: 800,
    fontSize: theme.typography.pxToRem(20),
    lineHeight: 1.3,
    color: theme.palette.text.primary,
  },
  subtitle: {
    marginTop: 2,
    color: theme.palette.text.secondary,
    fontSize: theme.typography.pxToRem(13),
  },
  callout: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor:
      theme.palette.type === "dark"
        ? theme.palette.background.default
        : theme.palette.primary[50] || "#F0F7FF",
  },
  calloutRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
    "&:last-child": { marginBottom: 0 },
  },
  calloutIcon: {
    color: theme.palette.primary.main,
    fontSize: 18,
    marginTop: 2,
  },
  successBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.success.light || theme.palette.success.main}`,
    backgroundColor:
      theme.palette.type === "dark"
        ? theme.palette.background.default
        : theme.palette.success[50] || "#E9FBF0",
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "flex-start",
  },
  actions: {
    padding: theme.spacing(1, 3, 2.5),
    gap: theme.spacing(1),
  },
}));

const statusColor = (status) => {
  if (status === "running") return "primary";
  if (status === "error") return "secondary";
  return "default";
};

const getTriggerIcon = (agent, triggerApps) => {
  const trigger = Array.isArray(agent?.triggers) && agent.triggers[0];
  if (!trigger) return null;
  if (trigger.app_icon) return trigger.app_icon;
  const app = (triggerApps || []).find((t) => t.app === trigger.source);
  return app?.icon ?? null;
};

const getActionIcons = (agent, actionApps) => {
  const actions = Array.isArray(agent?.actions) ? agent.actions : [];
  const seen = new Set();
  return actions
    .map((action) => {
      const url = action.app_icon || (actionApps || []).find((a) => a.app === action.app)?.icon;
      return url ? { url, app: action.app || url } : null;
    })
    .filter(Boolean)
    .filter(({ app }) => {
      if (seen.has(app)) return false;
      seen.add(app);
      return true;
    });
};

const AgentsPage = () => {
  const theme = useTheme();
  const dialogClasses = useDialogStyles();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const history = useHistory();
  const dispatch = useDispatch();
  const triggerApps = useSelector(selectAllTriggers);
  const actionApps = useSelector(selectAllActions);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [agents, setAgents] = useState([]);
  const [usage, setUsage] = useState(null);
  const [usageLoading, setUsageLoading] = useState(true);
  const [aiUsage, setAiUsage] = useState(null);
  const [aiUsageLoading, setAiUsageLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [createMode, setCreateMode] = useState("ai");
  const [prompt, setPrompt] = useState("");
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuAgentId, setMenuAgentId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteConfirmAgentId, setDeleteConfirmAgentId] = useState(null);
  const [cloneOpen, setCloneOpen] = useState(false);
  const [cloneAgentId, setCloneAgentId] = useState(null);
  const [cloneTenants, setCloneTenants] = useState([]);
  const [cloneSelectedIds, setCloneSelectedIds] = useState([]);
  const [cloneLoadingTenants, setCloneLoadingTenants] = useState(false);
  const [cloning, setCloning] = useState(false);
  const [cloneError, setCloneError] = useState(null);
  const [cloneResult, setCloneResult] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareAgentId, setShareAgentId] = useState(null);
  const [shareEmails, setShareEmails] = useState([]);
  const [shareInputValue, setShareInputValue] = useState("");
  const [sharing, setSharing] = useState(false);
  const [shareError, setShareError] = useState(null);
  const [shareResult, setShareResult] = useState(null);

  useEffect(() => {
    if (!triggerApps || triggerApps.length === 0) {
      dispatch(fetchAllApps(1));
    }
  }, [dispatch, triggerApps]);

  const templates = [
    "Job Lifecycle Supervisor",
    "Lead Follow-up Assistant",
    "Invoice Collection Monitor",
    "Support Escalation Agent",
    "Daily Summary Agent",
  ];

  const load = async () => {
    setLoading(true);
    try {
      // Do not await usage here — getAgentUsage can fan out /runs per agent and block the list.
      const result = await listAgents();
      setAgents(result || []);
    } finally {
      setLoading(false);
    }
    // Do not flip usageLoading back to true on refresh — keep the prior meter mounted
    // so agent cards don't jump. Skeleton only shows on the initial empty state.
    getAgentUsage({ allowPerAgentFanOut: false })
      .then((usageResult) => setUsage(usageResult))
      .catch(() => setUsage(null))
      .finally(() => setUsageLoading(false));
    fetchAiUsage()
      .then((aiResult) => setAiUsage(aiResult))
      .catch(() => setAiUsage(null))
      .finally(() => setAiUsageLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const promptFromUrlStarted = useRef(false);
  useEffect(() => {
    if (promptFromUrlStarted.current) return;
    const params = new URLSearchParams(window.location.search);
    const fromUrl = (params.get("prompt") || "").trim();
    if (!fromUrl) return;
    promptFromUrlStarted.current = true;
    params.delete("prompt");
    const qs = params.toString();
    window.history.replaceState(
      window.history.state,
      "",
      `${window.location.pathname}${qs ? `?${qs}` : ""}${window.location.hash || ""}`,
    );
    setPrompt(fromUrl);
    setCreateMode("ai");
    setCreating(true);
    createAgent({
      name: "AI Draft Agent",
      status: "draft",
      goal: fromUrl,
    })
      .then((created) => {
        history.push({
          pathname: `/agents/${created.agent_id}`,
          search: window.location.search,
          state: { initialPrompt: fromUrl, entryMode: "ai" },
        });
      })
      .catch(() => {
        setCreateOpen(true);
      })
      .finally(() => setCreating(false));
  }, [history]);

  const onCreateManual = async () => {
    setCreating(true);
    try {
      const created = await createAgent({ name: "Untitled Agent", status: "draft" });
      setCreateOpen(false);
      history.push({
        pathname: `/agents/${created.agent_id}`,
        state: { entryMode: "manual" },
      });
    } finally {
      setCreating(false);
    }
  };

  const onCreateAi = async () => {
    if (!prompt.trim()) return;
    setCreating(true);
    try {
      const created = await createAgent({
        name: "AI Draft Agent",
        status: "draft",
        goal: prompt.trim(),
      });
      setCreateOpen(false);
      history.push({
        pathname: `/agents/${created.agent_id}`,
        state: { initialPrompt: prompt.trim(), entryMode: "ai" },
      });
    } finally {
      setCreating(false);
    }
  };

  const openMenu = (event, agentId) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setMenuAgentId(agentId);
  };

  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuAgentId(null);
  };

  const openDeleteConfirm = () => {
    if (menuAgentId) {
      setDeleteConfirmAgentId(menuAgentId);
      setDeleteConfirmOpen(true);
    }
    closeMenu();
  };

  const openCloneDialog = async () => {
    if (!menuAgentId) {
      closeMenu();
      return;
    }
    const agentId = menuAgentId;
    closeMenu();
    setCloneAgentId(agentId);
    setCloneSelectedIds([]);
    setCloneError(null);
    setCloneResult(null);
    setCloneOpen(true);
    setCloneLoadingTenants(true);
    try {
      const tenants = await listAccessibleTenants({ excludeCurrent: true });
      setCloneTenants(tenants);
    } catch (err) {
      setCloneTenants([]);
      setCloneError(err?.message || "Failed to load workspaces.");
    } finally {
      setCloneLoadingTenants(false);
    }
  };

  const isValidShareEmail = (value) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());

  const normalizeShareEmail = (value) => String(value || "").trim().toLowerCase();

  /** Commit typed text (comma/space/enter) into the chip list. */
  const commitShareEmailInput = (raw) => {
    const parts = String(raw || "")
      .split(/[\s,;]+/)
      .map(normalizeShareEmail)
      .filter(Boolean);
    if (!parts.length) return;
    const invalid = parts.filter((e) => !isValidShareEmail(e));
    if (invalid.length) {
      setShareError(`Invalid email${invalid.length === 1 ? "" : "s"}: ${invalid.join(", ")}`);
      return;
    }
    setShareError(null);
    setShareEmails((prev) => [...new Set([...prev, ...parts])]);
    setShareInputValue("");
  };

  const openShareDialog = () => {
    if (!menuAgentId) {
      closeMenu();
      return;
    }
    setShareAgentId(menuAgentId);
    setShareEmails([]);
    setShareInputValue("");
    setShareError(null);
    setShareResult(null);
    setShareOpen(true);
    closeMenu();
  };

  const closeShareDialog = () => {
    if (sharing) return;
    setShareOpen(false);
    setShareAgentId(null);
    setShareEmails([]);
    setShareInputValue("");
    setShareError(null);
    setShareResult(null);
  };

  const onConfirmShareAgent = async () => {
    const pending = normalizeShareEmail(shareInputValue);
    let emails = [...shareEmails];
    if (pending) {
      if (!isValidShareEmail(pending)) {
        setShareError("Enter a valid email address.");
        return;
      }
      emails = [...new Set([...emails, pending])];
      setShareEmails(emails);
      setShareInputValue("");
    }
    if (!shareAgentId || emails.length === 0) return;
    setSharing(true);
    setShareError(null);
    setShareResult(null);
    try {
      let sharerName = "";
      try {
        const tok = localStorage.getItem("tok_");
        if (tok) {
          const me = await fetch("https://backend.workload.co/api/auth/me", {
            headers: { Authorization: `Bearer ${tok}` },
          }).then((r) => r.json());
          sharerName = me?.fullName || me?.full_name || me?.name || me?.email || "";
        }
      } catch (_) {
        /* optional */
      }
      const outcome = await shareAgentByEmail(shareAgentId, {
        emails,
        sharerName,
      });
      setShareResult(outcome);
      const results = Array.isArray(outcome?.results) ? outcome.results : [];
      const failedMail = results.filter((r) => !r.email_sent);
      if (failedMail.length && failedMail.length === results.length) {
        setShareError(
          outcome.email_reason === "missing_api_key"
            ? "Invites created, but emails could not be sent (SendGrid not configured on server)."
            : `Invites created, but emails were not sent (${outcome.email_reason || "skipped"}).`
        );
      } else if (failedMail.length) {
        setShareError(
          `Sent ${outcome.sent_count || results.length - failedMail.length} of ${
            results.length
          }. Failed for: ${failedMail.map((r) => r.recipient_email).join(", ")}`
        );
      }
    } catch (err) {
      setShareError(err?.response?.data?.error || err?.message || "Share failed.");
    } finally {
      setSharing(false);
    }
  };

  const closeCloneDialog = () => {
    if (cloning) return;
    setCloneOpen(false);
    setCloneAgentId(null);
    setCloneSelectedIds([]);
    setCloneError(null);
    setCloneResult(null);
  };

  const cloneSelectedTenants = cloneTenants.filter((t) => cloneSelectedIds.includes(t.id));
  const shareAgentName =
    agents.find((a) => a.agent_id === shareAgentId)?.name || "Untitled Agent";
  const cloneAgentName =
    agents.find((a) => a.agent_id === cloneAgentId)?.name || "Untitled Agent";

  const onConfirmCloneAgent = async () => {
    if (!cloneAgentId || cloneSelectedIds.length === 0) return;
    setCloning(true);
    setCloneError(null);
    setCloneResult(null);
    try {
      const outcome = await cloneAgentToTenants(cloneAgentId, cloneSelectedIds);
      setCloneResult(outcome);
      const failed = (outcome?.results || []).filter((r) => !r.ok);
      if (failed.length === (outcome?.results || []).length) {
        setCloneError("Clone failed for all selected workspaces.");
      }
    } catch (err) {
      setCloneError(err?.message || "Clone failed.");
    } finally {
      setCloning(false);
    }
  };

  const closeDeleteConfirm = () => {
    if (!deleting) {
      setDeleteConfirmOpen(false);
      setDeleteConfirmAgentId(null);
    }
  };

  const onConfirmDeleteAgent = async () => {
    if (!deleteConfirmAgentId) return;
    setDeleting(true);
    try {
      await deleteAgent(deleteConfirmAgentId);
      await load();
      closeDeleteConfirm();
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <AgentsAppBar title="Agents" />
      <div
      style={{
        padding: isMobile
          ? `${AGENTS_APP_BAR_OFFSET_PX}px 12px 16px 12px`
          : `${AGENTS_APP_BAR_OFFSET_PX}px 24px 24px 24px`,
        minHeight: "100vh",
        width: "100%",
        flex: 1,
        boxSizing: "border-box",
        backgroundColor: theme.palette.background.default,
        color: theme.palette.text.primary,
      }}
    >
      <Box maxWidth={1200} mx="auto">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gridGap={8}>
        <Typography variant="body2" color="textSecondary">
          Persistent goal-driven automations with deterministic rules.
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
          disabled={creating}
        >
          New agent
        </Button>
      </Box>

      {usage ? (
        <AgentUsageMeter usage={usage} onViewDetails={() => history.push("/agents/usage")} />
      ) : usageLoading ? (
        <AgentUsageMeterSkeleton />
      ) : null}

      {aiUsage ? (
        <AgentAiCreditMeter usage={aiUsage} />
      ) : aiUsageLoading ? (
        <AgentAiCreditMeterSkeleton />
      ) : null}

      {loading ? (
        <Box display="flex" justifyContent="center" mt={6}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={2}>
          {(agents || []).map((agent) => (
            <Grid item xs={12} md={6} lg={4} key={agent.agent_id}>
              <Card
                style={{ cursor: "pointer", height: "100%", display: "flex", flexDirection: "column" }}
                onClick={() => history.push(`/agents/${agent.agent_id}`)}
              >
                <CardContent style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
                  <Box
                    display="flex"
                    alignItems="flex-start"
                    justifyContent="space-between"
                    mb={1}
                    style={{ minHeight: 48 }}
                  >
                    <Typography variant="h6" style={{ flex: 1, minWidth: 0, marginRight: 8 }}>
                      {agent.name || "Untitled Agent"}
                    </Typography>
                    <Box display="flex" alignItems="center" style={{ gap: 4, flexShrink: 0 }}>
                      <Chip size="small" label={agent.status || "draft"} color={statusColor(agent.status)} />
                      <IconButton
                        size="small"
                        aria-label="More options"
                        aria-controls={menuAgentId === agent.agent_id ? "agent-card-menu" : undefined}
                        aria-haspopup="true"
                        aria-expanded={menuAgentId === agent.agent_id ? "true" : undefined}
                        onClick={(e) => openMenu(e, agent.agent_id)}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                  <Typography variant="body2" color="textSecondary" gutterBottom style={{ flex: "1 1 auto", minHeight: 0 }}>
                    {agent.description || "No description yet."}
                  </Typography>
                  <Box mt="auto" pt={1}>
                    {(getTriggerIcon(agent, triggerApps) || getActionIcons(agent, actionApps).length > 0) ? (
                      <Box display="flex" alignItems="center" flexWrap="wrap" mb={1} style={{ gap: 8 }}>
                        {getTriggerIcon(agent, triggerApps) ? (
                          <Avatar
                            src={getTriggerIcon(agent, triggerApps)}
                            style={{ width: 24, height: 24 }}
                            alt="Trigger"
                          />
                        ) : null}
                        {getTriggerIcon(agent, triggerApps) && getActionIcons(agent, actionApps).length > 0 ? (
                          <Typography variant="caption" color="textSecondary" style={{ marginRight: 4 }}>
                            →
                          </Typography>
                        ) : null}
                        {getActionIcons(agent, actionApps).length > 0 ? (
                          <AvatarGroup max={6} style={{ alignItems: "center" }}>
                            {getActionIcons(agent, actionApps).map(({ url, app }) => (
                              <Avatar
                                key={app}
                                src={url}
                                style={{ width: 24, height: 24 }}
                                alt="Action"
                              />
                            ))}
                          </AvatarGroup>
                        ) : null}
                      </Box>
                    ) : null}
                    <Typography variant="caption" color="textSecondary">
                      Updated {new Date(agent.updated_at || agent.created_at).toLocaleString()}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
          {agents.length === 0 ? (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="textSecondary">
                    No agents yet. Create your first agent to start configuring watches, rules, and actions.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ) : null}
        </Grid>
      )}
      </Box>

      <Menu
        id="agent-card-menu"
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        getContentAnchorEl={null}
      >
        <MenuItem onClick={openCloneDialog}>
          <ListItemIcon>
            <FileCopyOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Clone agent" />
        </MenuItem>
        <MenuItem onClick={openShareDialog}>
          <ListItemIcon>
            <ShareOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Share agent" />
        </MenuItem>
        <MenuItem onClick={openDeleteConfirm}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Delete agent" />
        </MenuItem>
      </Menu>

      <Dialog
        open={cloneOpen}
        onClose={closeCloneDialog}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={cloning}
        PaperProps={{ className: dialogClasses.paper, elevation: 0 }}
      >
        <div className={dialogClasses.accentBar} />
        <DialogTitle disableTypography>
          <Box display="flex" alignItems="flex-start" justifyContent="space-between">
            <div className={dialogClasses.headerRow}>
              <Avatar src={WORKLOAD_LOGO} alt="Workload" className={dialogClasses.logo} />
              <Box minWidth={0}>
                <Typography className={dialogClasses.title}>Clone agent</Typography>
                <Typography className={dialogClasses.subtitle}>
                  Copy “{cloneAgentName}” into other workspaces
                </Typography>
              </Box>
            </div>
            <IconButton
              size="small"
              aria-label="Close"
              onClick={closeCloneDialog}
              disabled={cloning}
              color="primary"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" paragraph>
            Search and select one or more workspaces. Credentials and run history are not copied —
            each destination connects their own accounts, previews, and starts.
          </Typography>
          {cloneLoadingTenants ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress size={28} color="primary" />
            </Box>
          ) : cloneTenants.length === 0 && !cloneError ? (
            <Typography variant="body2" color="textSecondary">
              No other workspaces are available to clone into.
            </Typography>
          ) : (
            <Autocomplete
              multiple
              options={cloneTenants}
              value={cloneSelectedTenants}
              onChange={(_e, value) => setCloneSelectedIds((value || []).map((t) => t.id))}
              getOptionLabel={(option) => option?.name || ""}
              getOptionSelected={(option, value) => option?.id === value?.id}
              disabled={cloning || Boolean(cloneResult)}
              disableCloseOnSelect
              filterSelectedOptions
              ListboxProps={{ style: { maxHeight: 280 } }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="Workspaces"
                  placeholder="Search workspaces…"
                  helperText={
                    cloneTenants.length
                      ? `${cloneSelectedIds.length} selected · ${cloneTenants.length} available`
                      : undefined
                  }
                />
              )}
            />
          )}
          {cloneError ? (
            <Typography variant="body2" color="error" style={{ marginTop: 12 }}>
              {cloneError}
            </Typography>
          ) : null}
          {cloneResult ? (
            <Box mt={2}>
              {(cloneResult.results || []).map((row) => {
                const tenantName =
                  cloneTenants.find((t) => t.id === row.tenant_id)?.name || row.tenant_id;
                return (
                  <Typography
                    key={row.tenant_id}
                    variant="body2"
                    style={{
                      color: row.ok ? theme.palette.success.main : theme.palette.error.main,
                      marginBottom: 4,
                      fontWeight: 500,
                    }}
                  >
                    {row.ok
                      ? `Cloned into ${tenantName}`
                      : `Failed for ${tenantName}: ${row.error || "unknown error"}`}
                  </Typography>
                );
              })}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions className={dialogClasses.actions}>
          <Button
            onClick={closeCloneDialog}
            disabled={cloning}
            variant="outlined"
            style={{
              borderColor: theme.palette.divider,
              color: theme.palette.text.secondary,
              fontWeight: 600,
            }}
          >
            {cloneResult ? "Done" : "Cancel"}
          </Button>
          {!cloneResult ? (
            <Button
              variant="contained"
              color="primary"
              onClick={onConfirmCloneAgent}
              disabled={cloning || cloneSelectedIds.length === 0 || cloneLoadingTenants}
              startIcon={
                cloning ? <CircularProgress size={16} color="inherit" /> : <FileCopyOutlinedIcon />
              }
              style={{ fontWeight: 700 }}
            >
              {cloning
                ? "Cloning…"
                : cloneSelectedIds.length === 0
                  ? "Clone"
                  : `Clone into ${cloneSelectedIds.length} workspace${
                      cloneSelectedIds.length === 1 ? "" : "s"
                    }`}
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>

      <Dialog
        open={shareOpen}
        onClose={closeShareDialog}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={sharing}
        PaperProps={{ className: dialogClasses.paper, elevation: 0 }}
      >
        <div className={dialogClasses.accentBar} />
        <DialogTitle disableTypography>
          <Box display="flex" alignItems="flex-start" justifyContent="space-between">
            <div className={dialogClasses.headerRow}>
              <Avatar src={WORKLOAD_LOGO} alt="Workload" className={dialogClasses.logo} />
              <Box minWidth={0}>
                <Typography className={dialogClasses.title}>Share agent</Typography>
                <Typography className={dialogClasses.subtitle}>
                  Invite people to use “{shareAgentName}”
                </Typography>
              </Box>
            </div>
            <IconButton
              size="small"
              aria-label="Close"
              onClick={closeShareDialog}
              disabled={sharing}
              color="primary"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" paragraph>
            Send one-time email invites. Each person gets their own copy in their workspace — your
            credentials and run history stay private.
          </Typography>
          <Box className={dialogClasses.callout}>
            <div className={dialogClasses.calloutRow}>
              <MailOutlineIcon className={dialogClasses.calloutIcon} />
              <Typography variant="body2" color="textSecondary">
                They open the invite, sign up or sign in, then pick a workspace if they have more
                than one.
              </Typography>
            </div>
            <div className={dialogClasses.calloutRow}>
              <ShareOutlinedIcon className={dialogClasses.calloutIcon} />
              <Typography variant="body2" color="textSecondary">
                Each link expires in 14 days and can only be used once.
              </Typography>
            </div>
          </Box>
          <Autocomplete
            multiple
            freeSolo
            options={[]}
            value={shareEmails}
            inputValue={shareInputValue}
            onChange={(_e, value) => {
              const next = [];
              const invalid = [];
              (value || []).forEach((item) => {
                const email = normalizeShareEmail(item);
                if (!email) return;
                if (!isValidShareEmail(email)) invalid.push(email);
                else if (!next.includes(email)) next.push(email);
              });
              if (invalid.length) {
                setShareError(
                  `Invalid email${invalid.length === 1 ? "" : "s"}: ${invalid.join(", ")}`
                );
              } else {
                setShareError(null);
              }
              setShareEmails(next);
            }}
            onInputChange={(_e, value, reason) => {
              if (reason === "input") {
                if (/[,\s;]/.test(value)) {
                  commitShareEmailInput(value);
                } else {
                  setShareInputValue(value);
                }
              } else if (reason === "clear") {
                setShareInputValue("");
              } else if (reason !== "reset") {
                setShareInputValue(value);
              }
            }}
            onBlur={() => {
              if (shareInputValue.trim()) commitShareEmailInput(shareInputValue);
            }}
            disabled={sharing || Boolean(shareResult?.ok)}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label="Recipient emails"
                placeholder={shareEmails.length ? "Add another…" : "name@company.com"}
                helperText="Press Enter or comma after each address. Up to 50 people."
                style={{ marginTop: 20 }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && shareInputValue.trim()) {
                    e.preventDefault();
                    e.stopPropagation();
                    commitShareEmailInput(shareInputValue);
                  }
                }}
              />
            )}
          />
          {shareError ? (
            <Typography variant="body2" color="error" style={{ marginTop: 12 }}>
              {shareError}
            </Typography>
          ) : null}
          {shareResult?.ok ? (
            <Box className={dialogClasses.successBox}>
              <CheckCircleOutlineIcon style={{ color: theme.palette.success.main, marginTop: 2 }} />
              <Box>
                <Typography variant="body2" color="textPrimary">
                  {`Invites created for ${
                    shareResult.invited_count || (shareResult.results || []).length || 1
                  } ${
                    (shareResult.invited_count || (shareResult.results || []).length || 1) === 1
                      ? "person"
                      : "people"
                  }.${
                    shareResult.sent_count != null ? ` ${shareResult.sent_count} emailed.` : ""
                  }`}
                </Typography>
                {(shareResult.results || []).length > 0 ? (
                  <Box mt={1}>
                    {(shareResult.results || []).map((row) => (
                      <Typography
                        key={row.share_token || row.recipient_email}
                        variant="caption"
                        display="block"
                        style={{
                          color: row.email_sent
                            ? theme.palette.text.secondary
                            : theme.palette.error.main,
                          marginBottom: 2,
                        }}
                      >
                        {row.recipient_email}
                        {row.email_sent
                          ? " — sent"
                          : ` — not sent${row.email_reason ? ` (${row.email_reason})` : ""}`}
                      </Typography>
                    ))}
                  </Box>
                ) : null}
              </Box>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions className={dialogClasses.actions}>
          <Button
            onClick={closeShareDialog}
            disabled={sharing}
            variant="outlined"
            style={{
              borderColor: theme.palette.divider,
              color: theme.palette.text.secondary,
              fontWeight: 600,
            }}
          >
            {shareResult?.ok ? "Done" : "Cancel"}
          </Button>
          {!shareResult?.ok ? (
            <Button
              variant="contained"
              color="primary"
              onClick={onConfirmShareAgent}
              disabled={sharing || (shareEmails.length === 0 && !shareInputValue.trim())}
              startIcon={
                sharing ? <CircularProgress size={16} color="inherit" /> : <ShareOutlinedIcon />
              }
              style={{ fontWeight: 700 }}
            >
              {sharing
                ? "Sending…"
                : `Send invite${
                    shareEmails.length + (shareInputValue.trim() ? 1 : 0) > 1 ? "s" : ""
                  }`}
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteConfirmOpen}
        onClose={closeDeleteConfirm}
        maxWidth="xs"
        fullWidth
        disableEscapeKeyDown={deleting}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">Delete agent?</Typography>
            <IconButton
              size="small"
              aria-label="Close"
              onClick={closeDeleteConfirm}
              disabled={deleting}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" color="textPrimary">
            Are you sure you want to delete this agent?
          </Typography>
        </DialogContent>
        <DialogActions style={{ padding: "8px 24px 16px" }}>
          <Button onClick={closeDeleteConfirm} disabled={deleting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={onConfirmDeleteAgent}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
            style={{
              backgroundColor: "#d32f2f",
              color: "#fff",
            }}
          >
            {deleting ? "Deleting…" : "Delete agent"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle>New agent</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" gutterBottom>
                Start options
              </Typography>
              <List dense>
                <ListItem button selected={createMode === "ai"} onClick={() => setCreateMode("ai")}>
                  <ListItemText primary="Create with AI builder" secondary="Describe what the agent should do." />
                </ListItem>
                <ListItem button selected={createMode === "manual"} onClick={() => setCreateMode("manual")}>
                  <ListItemText primary="Start from scratch" secondary="Build manually with structured sections." />
                </ListItem>
              </List>
              <Divider style={{ margin: "8px 0" }} />
              <Typography variant="subtitle2" gutterBottom>
                Template ideas
              </Typography>
              <List dense>
                {templates.map((name) => (
                  <ListItem key={name} button onClick={() => { setCreateMode("ai"); setPrompt(`Create an agent like ${name} for my team.`); }}>
                    <ListItemText primary={name} />
                  </ListItem>
                ))}
              </List>
            </Grid>
            <Grid item xs={12} md={8}>
              {createMode === "ai" ? (
                <>
                  <Typography variant="h6" gutterBottom>
                    Create with AI builder
                  </Typography>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Describe the workflow in plain English. You will review and edit everything before starting.
                  </Typography>
                  <TextField
                    fullWidth
                    multiline={true}
                    rows={8}
                    variant="outlined"
                    label="Instructions"
                    placeholder="Monitor X and alert me when Y happens. If Z, do A."
                    value={prompt}
                    onChange={(event) => setPrompt(event.target.value)}
                  />
                  <Box mt={2} display="flex" justifyContent="flex-end">
                    <Button variant="contained" color="primary" disabled={creating || !prompt.trim()} onClick={onCreateAi}>
                      Start building
                    </Button>
                  </Box>
                </>
              ) : (
                <>
                  <Typography variant="h6" gutterBottom>
                    Start from scratch
                  </Typography>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    We will open a clean manual editor with collapsible sections for watches, rules, tools, and safety.
                  </Typography>
                  <Box mt={2} display="flex" justifyContent="flex-end">
                    <Button variant="contained" color="primary" disabled={creating} onClick={onCreateManual}>
                      Create blank agent
                    </Button>
                  </Box>
                </>
              )}
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
    </div>
    </>
  );
};

export default AgentsPage;
